import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';

import { Accordion, AccordionChild, Icon } from '@holmescorp/base-widgets';
import { TopicDef, TopicDetails, ParentTree } from 'lcc-state/interfaces';
import { pathmap, keymap, userstate, createNlsMiddleware } from 'lcc-state';
import { Keys } from '@holmescorp/common';
import { publish } from '@holmescorp/pub-sub';

import css from '../../css/topics.m.css';

export interface GroupNavProperties {
	topicset: TopicDetails;
	currentTopicGroupKey?: string;
}

const factory = create({
	theme,
	userstate,
	nls: createNlsMiddleware('ditabook')
}).properties<GroupNavProperties>();

export const GroupNav = factory(({ properties, middleware: { theme, userstate, nls } }) => {
	const { currentTopicGroupKey = '', topicset } = properties();
	const { get, path } = userstate;
	const {
		messages: { topic_group_section_label = 'Lesson' }
	} = nls();

	const themeCss = theme.classes(css);

	const status: ParentTree = get(path('status'));

	const topicGroups = topicset.children?.filter((tg) => !('shortdesc' in tg)) || [];

	const generateLesson = (topic: TopicDef, topicgroupKey: string) => {
		const topicId = topic.topicref.attributes?.rel || '';
		const topicKeyRef = topic.topicref.attributes?.id || '';
		const topicStatus = status[topicId].data;
		return v(
			'div',
			{
				classes: [themeCss.accordionContentRow],
				key: `${topicgroupKey}-${topicId}`,
				xqa: `topic ${topicId}`,
				role: 'button',
				tabIndex: 0,
				onclick: (event: MouseEvent) => {
					event.stopPropagation();
					if (currentTopicGroupKey === topicgroupKey) {
						publish('scroll-to-topic', topicKeyRef);
					} else {
						const { path } = keymap.get(topicgroupKey)!;
						const { view } = pathmap.get(path)!;
						view();
					}
				},
				onkeydown: (event: KeyboardEvent) => {
					switch (event.key) {
						case Keys.Enter:
						case Keys.Space:
						case Keys.Spacebar:
							if (currentTopicGroupKey === topicgroupKey) {
								publish('scroll-to-topic', topicKeyRef);
							} else {
								const { path } = keymap.get(topicgroupKey)!;
								const { view } = pathmap.get(path)!;
								view();
							}
					}
				}
			},
			[
				v(
					'div',
					{
						classes: [themeCss.lessonTitleRow]
					},
					[
						topicStatus.complete > 0
							? w('TopicStatusIcon', {
									key: topic.topicref.attributes?.rel,
									useQuizScore: false,
									quizCompleteOnly: false
							  })
							: null,
						v('book', {}, [w(Icon, { faname: 'book-open', fapre: 'far' })]),
						v('span', {}, [topic.topicref.attributes?.title])
					]
				),
				v('span', { classes: [themeCss.readingTime] }, [`${topic.topicref.attributes?.readtime} mins`])
			]
		);
	};

	const accordionContent =
		topicGroups.map((tg, index): AccordionChild => {
			return {
				key: `topicgroup-${tg.topicgroup.attributes?.key}`,
				content: tg.topicgroup.children
					?.filter((t) => !('shortdesc' in t))
					.map((topic) => generateLesson(topic, tg.topicgroup.attributes?.key || '')),
				title: [
					v(
						'div',
						{
							xqa: `topicgroup ${tg.topicgroup.attributes?.key}`,
							classes: [themeCss.accordionTitleContainer]
						},
						[
							v('p', { classes: [themeCss.accordionTitle] }, [
								v('span', { classes: [themeCss.accordionGroupNumber] }, [
									`${topic_group_section_label} ${index + 1}`
								]),
								v('span', { classes: [themeCss.accordionGroupTitle] }, [
									`${tg.topicgroup.attributes?.title}`
								])
							])
						]
					)
				]
			};
		}) || [];

	return v('div', { classes: [themeCss.accordion] }, [
		w(
			Accordion,
			{
				exclusive: true,
				headingLevel: 1,
				openKeys: [`topicgroup-${currentTopicGroupKey}`]
			},
			accordionContent
		)
	]);
});
export default GroupNav;
